import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Select,
  Spinner,
  Stack,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tag,
  TagLabel,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useColorModeValue,
  useDisclosure,
  Radio,
  RadioGroup,
} from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import { capitalizeFirstLetter, getBackendURL, apiFetch, getBackendAssetURL } from '../../../utils/utils';
import { FiPlusSquare } from 'react-icons/all';
import { BaseLocatization } from '../../../modules/base-locatization/base-locatization';
import { useToast } from '@chakra-ui/react';

function DistributorPage() {

  const [settings, setSettings] = useState(null);
  const [qrCodeImg, setQrCodeImg] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  const [distributor, setDistributor] = useState({});
  const [error, setError] = useState('');
  const [isRequestCompleted, setIsRequestCompleted] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [actionStatus, setActionStatus] = useState('');

  const [distributors, setDistributors] = useState([]);
  const [plans, setPlans] = useState([]);
  const [distributorSelectedPlan, setDistributorSelectedPlan] = useState('');
  const [distributorAmount, setDistributorAmount] = useState(0);
  const [distributorPaymentTrackingNumber, setDistributorTrackingNumber] = useState('');
  const [isDisableRequest, setIsDisableRequest] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [paymentMethod, setPaymentMethod] = useState('OFFLINE');
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  let navigate = useNavigate();
  const toast = useToast();

  const markDistributorAsPaid = (id, txnId) => {
    setInProgress(true);
    apiFetch(`${getBackendURL()}/admin/distributor/distributors/mark-as-paid`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, txnId }),
    })
      .then(res => res.json())
      .then(res => {
        setInProgress(false);
        fetchDistributors();
        toast({ title: 'Success', description: res.message, status: 'success', duration: 3000 });
      })
      .catch(err => {
        setInProgress(false);
        console.error(err);
      });
  };

  const [resendConfirmDistributor, setResendConfirmDistributor] = useState(null);
  const [resendInProgress, setResendInProgress] = useState(false);
  const resendCancelRef = useRef();

  const openResendConfirm = (distributor) => {
    setResendConfirmDistributor(distributor);
  };

  const confirmResendDistributorPaymentLink = () => {
    if (!resendConfirmDistributor) return;
    setResendInProgress(true);
    apiFetch(`${getBackendURL()}/admin/distributor/distributors/resend-payment-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: resendConfirmDistributor.id }),
    })
      .then(res => res.json())
      .then(res => {
        setResendInProgress(false);
        setResendConfirmDistributor(null);
        fetchDistributors();
        toast({ title: 'Success', description: res.message, status: 'success', duration: 3000 });
      })
      .catch(err => {
        setResendInProgress(false);
        setResendConfirmDistributor(null);
        console.error(err);
        toast({ title: 'Error', description: 'Failed to resend payment link', status: 'error', duration: 3000 });
      });
  };

  const fetchPlans = useCallback(() => {
    apiFetch(`${getBackendURL()}/admin/distributor/plans`)
      .then(response => response.json())
      .then(response => {
        if (response !== null && response.plans.length > 0) {
          let plans = [];
          response.plans.forEach(plan => {
            plans.push({
              id: plan._id,
              name: plan.name,
              price: plan.price,
              autoSMSProfiles: plan.autoSMSProfiles,
              digiCardProfiles: plan.digiCardProfiles,
            });
          });
          setPlans(plans);
        } else {
          setPlans([]);
        }
      })
      .catch(error => {
        console.log(error);
        setPlans([]);
      });
  }, []);

  const fetchSettings = useCallback(() => {
    apiFetch(`${getBackendURL()}/admin/settings`)
      .then(res => res.json())
      .then(data => {
        setSettings(data);
        if (data.qrCodePath) {
          setQrCodeImg(data.qrCodePath.startsWith('http') ? data.qrCodePath : `${getBackendAssetURL()}/${data.qrCodePath}`);
        }
      })
      .catch(err => console.error('Error fetching settings:', err));
  }, []);

  const fetchDistributors = useCallback(() => {
    setIsLoading(true);
    apiFetch(`${getBackendURL()}/admin/distributor/distributors?search=${search}&page=${page}&limit=${limit}`)
      .then(response => response.json())
      .then(response => {
        if (response !== null && response.distributors && response.distributors.length > 0) {
          let distributors = [];
          response.distributors.forEach(distributor => {
            distributors.push({
              id: distributor._id,
              displayName: distributor.displayName,
              username: distributor.username,
              planName: distributor.planName,
              isActive: distributor.isActive,
              paymentStatus: distributor.paymentStatus || 'Paid',
              digiCardCount: distributor.digiCardCount,
              merchantCount: distributor.merchantCount,
              pendingPaymentType: distributor.pendingPaymentType,
            });
          });
          setDistributors(distributors);
          setTotal(response.total);
        } else {
          setDistributors([]);
          setTotal(0);
        }
        setIsLoading(false);
      })
      .catch(error => {
        console.log(error);
        setDistributors([]);
        setTotal(0);
        setIsLoading(false);
      });
  }, [search, page, limit]);

  useEffect(() => {
    fetchDistributors();
    fetchPlans();
    fetchSettings();
  }, [fetchDistributors, fetchPlans, fetchSettings]);

  const addProfilesToDistributor = () => {
    setError('');
    if (
      distributorSelectedPlan.id === undefined ||
      distributorAmount === 0 ||
      (paymentMethod === 'OFFLINE' && distributorPaymentTrackingNumber === '')
    ) {
      setError('Please set the proper details');
      return;
    }
    setInProgress(true);
    apiFetch(`${getBackendURL()}/admin/distributor/distributors/plan/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: distributor.id,
        planId: distributorSelectedPlan.id,
        amount: distributorAmount,
        txnNumber: distributorPaymentTrackingNumber,
        paymentMethod: paymentMethod,
      }),
    })
      .then(response => response.json())
      .then(response => {
        if (response.updated) {
          setActionStatus(response.message);
        } else {
          setError(response.message);
        }
        setDistributor({});
        setDistributorSelectedPlan({});
        setDistributorAmount(0);
        setDistributorTrackingNumber('');
        setInProgress(false);
        setIsRequestCompleted(true);
        fetchDistributors();
      })
      .catch(error => {
        console.log(error);
        setInProgress(false);
        setError(error);
      });
  };

  const updateDistributorStatus = () => {
    setError('');
    setInProgress(true);
    apiFetch(`${getBackendURL()}/admin/distributor/distributors/toggle`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: distributor.id,
        isActive: !distributor.isActive,
      }),
    })
      .then(response => response.json())
      .then(response => {
        if (response.updated) {
          setActionStatus(response.message);
        } else {
          setError(response.message);
        }
        setDistributor({});
        setInProgress(false);
        setIsRequestCompleted(true);
        fetchDistributors();
      })
      .catch(error => {
        console.log(error);
        setInProgress(false);
        setError(error);
      });
  };

  const setSelectedPlan = id => {
    if (!id) {
      setDistributorSelectedPlan({});
      return;
    }
    let plan = plans.filter(plan => plan.id === id);
    setDistributorAmount(plan[0].price);
    setDistributorSelectedPlan(plan[0]);
  };

  const onAddProfiles = index => {
    let distributorToEdit = distributors[index];
    setDistributor(distributorToEdit);
    setIsDisableRequest(false);
    setInProgress(false);
    setIsRequestCompleted(false);
    setActionStatus('');
    setError('');
    setPaymentMethod('OFFLINE');
    setDistributorTrackingNumber('');
    onOpen();
  };

  const onDisable = index => {
    let distributorToDisable = distributors[index];
    setDistributor(distributorToDisable);
    setIsDisableRequest(true);
    setInProgress(false);
    setIsRequestCompleted(false);
    setActionStatus('');
    setError('');
    onOpen();
  };

  return (
    <>
      <Breadcrumb marginTop={2} marginLeft={2}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/">
            {BaseLocatization.Home}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/distributors">
            {BaseLocatization.Distributors}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <Stack mt={4} direction={{ base: 'column', lg: 'row' }}>
        <Flex flex={1}>
          <Box w={'100%'} maxW="100%" overflow="hidden">
            <Card bg={useColorModeValue('white', 'gray.700')}>
              <CardHeader>
                <Flex spacing="4" alignItems="center" flexWrap="wrap">
                  <Heading size="md" mb={{ base: 2, md: 0 }}>
                    {BaseLocatization.Distributors}
                  </Heading>
                  <Input
                    placeholder="Search name or email..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    mx={{ base: 0, md: 4 }}
                    maxW="300px"
                    size="sm"
                  />
                  <Button
                    ml="auto"
                    leftIcon={<FiPlusSquare />}
                    colorScheme="teal"
                    variant="solid"
                    onClick={() => navigate('add')}
                    size="sm"
                  >
                    {BaseLocatization.AddDistributor}
                  </Button>
                </Flex>
              </CardHeader>
              <Divider />
              <CardBody>
                <TableContainer data-testid="plans-table" maxW="100%" overflowX="auto">
                  {isLoading ? (
                    <Flex justify="center" align="center" minH="200px">
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color="blue.500"
                        size="xl"
                      />
                    </Flex>
                  ) : (
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Sr. No</Th>
                          <Th>Name</Th>
                          <Th>Status</Th>
                          <Th>Payment Status</Th>
                          <Th>Associated <br /> Merchants</Th>
                          <Th>Used <br /> Profiles</Th>
                          <Th>Remaining <br /> Profiles</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {distributors.map((distributor, index) => {
                          return (
                            <Tr key={distributor.id}>
                              <Td>{(page - 1) * limit + index + 1}</Td>
                              <Td>
                                {capitalizeFirstLetter(
                                  distributor.displayName || distributor.username
                                )}
                              </Td>
                              <Td>
                                <Tag
                                  size="sm"
                                  colorScheme={distributor.isActive ? 'green' : 'red'}
                                  borderRadius="full"
                                >
                                  <TagLabel>
                                    {distributor.isActive ? 'Active' : 'Inactive'}
                                  </TagLabel>
                                </Tag>
                              </Td>
                              <Td>
                                <Tag
                                  size="sm"
                                  colorScheme={
                                    distributor.paymentStatus?.toLowerCase() === 'paid' ? 'green' :
                                      distributor.paymentStatus?.toLowerCase() === 'pending' ? 'yellow' : 'red'
                                  }
                                  variant="subtle"
                                >
                                  <TagLabel>
                                    {distributor.paymentStatus?.toLowerCase() === 'pending' && distributor.pendingPaymentType === 'UPGRADE'
                                      ? 'Pending (Upgrade)'
                                      : distributor.paymentStatus}
                                  </TagLabel>
                                </Tag>
                              </Td>
                              <Td>
                                <Box>
                                  <Text fontWeight="bold" fontSize="sm">
                                    {distributor.merchantCount}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    Merchants
                                  </Text>
                                </Box>
                              </Td>
                              <Td>
                                <Box>
                                  <Text fontWeight="bold" fontSize="sm">
                                    {distributor.digiCardCount.usedProfiles}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {BaseLocatization.DigiCard}
                                  </Text>
                                </Box>
                              </Td>
                              <Td>
                                <Box>
                                  <Text fontWeight="bold" fontSize="sm">
                                    {distributor.digiCardCount.totalProfiles -
                                      distributor.digiCardCount.usedProfiles}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {BaseLocatization.DigiCard}
                                  </Text>
                                </Box>
                              </Td>
                              <Td>
                                <Stack direction="column" spacing={1}>
                                  {!(distributor.paymentStatus?.toLowerCase() === 'pending' && distributor.pendingPaymentType === 'UPGRADE') && (
                                    <Button
                                      size="xs"
                                      colorScheme="teal"
                                      variant="outline"
                                      onClick={() => onAddProfiles(index)}
                                    >
                                      {BaseLocatization.AddProfiles}
                                    </Button>
                                  )}
                                  <Button
                                    size="xs"
                                    colorScheme={distributor.isActive ? 'red' : 'green'}
                                    variant="outline"
                                    onClick={() => onDisable(index)}
                                  >
                                    {distributor.isActive ? 'Deactivate' : 'Activate'}
                                  </Button>
                                  {(distributor.paymentStatus?.toLowerCase() === 'pending' || distributor.paymentStatus?.toLowerCase() === 'failed') && (
                                    <>
                                      <Button
                                        size="xs"
                                        colorScheme="blue"
                                        onClick={() => {
                                          const txnId = window.prompt('Enter manual Transaction ID:');
                                          if (txnId) markDistributorAsPaid(distributor.id, txnId);
                                        }}
                                      >
                                        Mark Paid
                                      </Button>
                                      <Button
                                        size="xs"
                                        colorScheme="purple"
                                        variant="outline"
                                        onClick={() => openResendConfirm(distributor)}
                                      >
                                        Resend Link
                                      </Button>
                                    </>
                                  )}
                                </Stack>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  )}
                </TableContainer>
              </CardBody>
              <Divider />
              <Flex p={4} justify="space-between" align="center" flexWrap="wrap" gap={2}>
                <Text fontSize="sm" color="gray.500">
                  Showing {total > 0 ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, total)} of {total} results
                </Text>
                <Flex gap={2}>
                  <Button
                    size="sm"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page * limit >= total}
                  >
                    Next
                  </Button>
                </Flex>
              </Flex>
            </Card>
          </Box>
        </Flex>
      </Stack>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        closeOnOverlayClick={false}
        size="lg"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {isDisableRequest ? 'Update status' : 'Edit Plan'}
            </AlertDialogHeader>
            {inProgress ? (
              <Box ml={'auto'} mr={'auto'} mt={3} mb={3}>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Box>
            ) : (
              <>
                {isRequestCompleted ? (
                  <>
                    <AlertDialogBody m={3}>
                      <Alert
                        status="success"
                        display={actionStatus ? 'flex' : 'none'}
                      >
                        <AlertIcon />
                        {actionStatus}
                      </Alert>
                      <Alert status="error" display={error ? 'flex' : 'none'}>
                        <AlertIcon />
                        {error}
                      </Alert>
                    </AlertDialogBody>
                    <AlertDialogFooter justifyContent={'center'}>
                      <Button ref={cancelRef} onClick={onClose}>
                        {error ? 'Retry' : 'Go back'}
                      </Button>
                    </AlertDialogFooter>
                  </>
                ) : (
                  <>
                    <AlertDialogBody>
                      {!isDisableRequest ? (
                        <Stack spacing="4" mt="2">
                          <FormControl isRequired>
                            <FormLabel>{BaseLocatization.Username}</FormLabel>
                            <Input
                              type="text"
                              id="displayName"
                              name="displayName"
                              value={distributor.displayName || distributor.username}
                              disabled
                            />
                          </FormControl>
                          <FormControl isRequired>
                            <FormLabel>{BaseLocatization.PlanName}</FormLabel>
                            <Select
                              placeholder="Select Plan"
                              onChange={event =>
                                setSelectedPlan(event.target.value)
                              }
                            >
                              {plans.map(plan => {
                                return (
                                  <option key={plan.id} value={plan.id}>
                                    {plan.name} (Rs.{plan.price})
                                  </option>
                                );
                              })}
                            </Select>
                          </FormControl>
                          <FormControl isRequired>
                            <FormLabel>{BaseLocatization.Amount}</FormLabel>
                            <Input
                              type="number"
                              id="amount"
                              name="amount"
                              value={distributorAmount}
                              disabled
                              onChange={event =>
                                setDistributorAmount(event.target.value)
                              }
                            />
                            <FormHelperText>
                              {distributorSelectedPlan.price !== undefined
                                ? `Please collect the amount of ${distributorSelectedPlan.price}`
                                : 'Plan price'}
                            </FormHelperText>
                          </FormControl>
                          <FormControl isRequired>
                            <FormLabel>Payment Method</FormLabel>
                            <RadioGroup onChange={setPaymentMethod} value={paymentMethod}>
                              <Stack direction="row" spacing={5}>
                                <Radio value="OFFLINE">Offline Payment</Radio>
                                <Radio value="ONLINE" isDisabled={settings?.paymentGatewayEnabled === false}>Online Payment</Radio>
                              </Stack>
                            </RadioGroup>
                          </FormControl>
                          {paymentMethod === 'OFFLINE' && (
                            <>
                              <FormControl isRequired>
                                <FormLabel>{BaseLocatization.PaymenttId}</FormLabel>
                                <Input
                                  type="text"
                                  id="trackingId"
                                  name="trackingId"
                                  value={distributorPaymentTrackingNumber}
                                  onChange={event =>
                                    setDistributorTrackingNumber(event.target.value)
                                  }
                                  placeholder="Enter offline transaction ID"
                                />
                              </FormControl>

                              {distributorSelectedPlan.id && (
                                <Box textAlign="center" p={4} border="1px dashed" borderColor="gray.300" borderRadius="md">
                                  <Text fontWeight="bold" mb={2}>Payment QR Code for {distributorSelectedPlan.name}</Text>
                                  <Flex justify="center">
                                    <Box bg="white" p={2} borderRadius="md" boxShadow="sm">
                                      <img
                                        src={qrCodeImg}
                                        alt="Payment QR Code"
                                        width="200"
                                        height="200"
                                      />
                                    </Box>
                                  </Flex>
                                  <Text fontSize="xs" mt={2} color="gray.500">Scan this QR to pay Rs.{distributorAmount}</Text>
                                </Box>
                              )}
                            </>
                          )}
                          {paymentMethod === 'ONLINE' && (
                            <Box p={3} bg="blue.50" borderRadius="md">
                              <Text fontSize="sm" color="blue.700">A payment link will be sent to the distributor's email.</Text>
                            </Box>
                          )}
                        </Stack>
                      ) : (
                        <>
                          {' '}
                          <>
                            {' '}
                            {BaseLocatization.ActivationStatusQuestion}
                            {distributor.isActive
                              ? `${BaseLocatization.Deactivate}`
                              : `${BaseLocatization.Activate}`}
                          </>
                        </>
                      )}
                    </AlertDialogBody>
                    <AlertDialogFooter>
                      <Button ref={cancelRef} onClick={onClose}>
                        {BaseLocatization.Cancel}
                      </Button>
                      {!isDisableRequest ? (
                        <Button
                          colorScheme="green"
                          onClick={addProfilesToDistributor}
                          ml={3}
                        >
                          Add
                        </Button>
                      ) : (
                        <Button
                          colorScheme={distributor.isActive ? 'red' : 'green'}
                          onClick={updateDistributorStatus}
                          ml={3}
                        >
                          {distributor.isActive
                            ? `${BaseLocatization.Deactivate}`
                            : `${BaseLocatization.Activate}`}
                        </Button>
                      )}
                    </AlertDialogFooter>
                  </>
                )}
              </>
            )}
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <AlertDialog
        isOpen={!!resendConfirmDistributor}
        leastDestructiveRef={resendCancelRef}
        onClose={() => !resendInProgress && setResendConfirmDistributor(null)}
        closeOnOverlayClick={false}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Resend Payment Link
            </AlertDialogHeader>
            {resendInProgress ? (
              <Box ml="auto" mr="auto" mt={6} mb={6}>
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="purple.500" size="xl" />
              </Box>
            ) : (
              <>
                <AlertDialogBody>
                  Are you sure you want to resend the payment link to{' '}
                  <strong>{resendConfirmDistributor?.displayName || resendConfirmDistributor?.username}</strong>?
                  <br />
                  If the existing link has expired, a new one will be generated automatically.
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={resendCancelRef} onClick={() => setResendConfirmDistributor(null)}>
                    Cancel
                  </Button>
                  <Button colorScheme="purple" onClick={confirmResendDistributorPaymentLink} ml={3}>
                    Yes, Resend
                  </Button>
                </AlertDialogFooter>
              </>
            )}
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default DistributorPage;
